import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { GuestApi } from '../api/client';
import { asFailure } from '../api/errors';
import type { SiteResponse } from '../wire';
import type { ResolvedTheme } from '../theme/theme';
import { clockFor } from './time';
import type { Clock } from './time';

/**
 * What every component on the page can see: the tenant, its catalog, the theme
 * that was resolved from its branding row, a clock pinned to its timezone, and
 * whether this browser currently holds a session.
 *
 * **The session is a server answer, not a cookie read.** The cookie is
 * `httpOnly` and this bundle could not read it; `signedIn` starts as
 * `site.session.authenticated` — the block `/api/site` already carries, so the
 * first paint costs no second request — and is refreshed by `probeSession()`,
 * which is a `GET /api/me`. Every place a `401` can arrive calls `expire()`, so
 * a cookie that ran out while a page sat open becomes an OTP prompt rather than
 * a button that silently does nothing.
 */

export interface GuestName {
  displayName: string;
  /** The identity that was proved, so the flow can prefill and not re-ask. */
  identity: string | null;
}

export interface SiteContextValue {
  api: GuestApi;
  site: SiteResponse['site'];
  services: SiteResponse['services'];
  theme: ResolvedTheme;
  clock: Clock;
  signedIn: boolean;
  guest: GuestName | null;
  /** `GET /api/me`; returns the guest, or `null` when there is no session. */
  probeSession: () => Promise<GuestName | null>;
  /** Called wherever a `401` surfaces. Never optimistic about a dead cookie. */
  expire: () => void;
  signOut: () => Promise<void>;
}

const SiteContext = createContext<SiteContextValue | null>(null);

export function SiteProvider({
  api,
  site,
  theme,
  children,
}: {
  api: GuestApi;
  site: SiteResponse;
  theme: ResolvedTheme;
  children: ReactNode;
}): ReactNode {
  const [signedIn, setSignedIn] = useState(site.session.authenticated);
  const [guest, setGuest] = useState<GuestName | null>(null);

  const probeSession = useCallback(async (): Promise<GuestName | null> => {
    try {
      const me = await api.me();
      const identity = me.guest.identities[0]?.identity ?? null;
      const value: GuestName = { displayName: me.guest.display_name, identity };
      setGuest(value);
      setSignedIn(true);
      return value;
    } catch (e) {
      // A `401` here is the ordinary end of a session and not an error worth
      // showing: the page simply goes back to asking who you are. Anything else
      // — offline, a 503 — leaves the flag alone rather than signing a guest out
      // because their train went into a tunnel.
      if (asFailure(e).code === 'unauthenticated') {
        setSignedIn(false);
        setGuest(null);
        return null;
      }
      throw e;
    }
  }, [api]);

  const expire = useCallback(() => {
    setSignedIn(false);
    setGuest(null);
  }, []);

  const signOut = useCallback(async () => {
    // Always answers 200 and always clears the cookie, even for a session the
    // server no longer accepts — so this never needs a try/catch for the case
    // that matters. A network failure still leaves the local flags true, which
    // is honest: the cookie is still in the browser.
    await api.endSession();
    setSignedIn(false);
    setGuest(null);
  }, [api]);

  const value = useMemo<SiteContextValue>(
    () => ({
      api,
      site: site.site,
      services: site.services,
      theme,
      clock: clockFor(site.site.timezone),
      signedIn,
      guest,
      probeSession,
      expire,
      signOut,
    }),
    [api, site, theme, signedIn, guest, probeSession, expire, signOut],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteContextValue {
  const value = useContext(SiteContext);
  if (value === null) throw new Error('useSite outside a SiteProvider');
  return value;
}
